import { Listbox } from '@headlessui/react';
import { Icon } from '../Icon';
import {
  errorTextClasses,
  helperTextClasses,
  labelClasses,
  listBoxButtonClasses,
  listBoxClasses,
  listBoxOptionClasses,
  listBoxOptionsClasses,
  selectGroupClasses,
} from './variants';
import { VariantProps } from 'tailwind-variants';
import Checkbox from '../Checkbox';
import './styles.css';

export type IMultipleOptions = {
  value: string;
  label: string;
};

interface IMultipleSelect extends VariantProps<typeof selectGroupClasses> {
  options: IMultipleOptions[];
  selected: string[];
  onChange: (value: string[]) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  className?: string;
}

const MultipleSelect: React.FC<IMultipleSelect> = (props) => {
  const {
    options,
    selected,
    onChange,
    label,
    placeholder,
    size,
    helperText,
    disabled = false,
    error,
    errorMessage,
    className,
  } = props;

  const hasErrorMessage = !!errorMessage;
  const hasError = error || !!errorMessage;

  const constructValue = () => {
    if (selected.length > 0) {
      const selectedOptions = options.filter((it) =>
        selected.includes(it.value)
      );

      return selectedOptions.map((it) => it.label).join('; ');
    }

    return placeholder || '';
  };

  const handleArray = (value: string) => {
    const aux = [...selected];
    const isInArray = aux.includes(value);

    if (isInArray) {
      const idx = aux.findIndex((it) => it === value);

      aux.splice(idx, 1);
      onChange(aux);
    } else {
      aux.push(value);
      onChange(aux);
    }
  };

  return (
    <div className={selectGroupClasses({ size, disabled, className })}>
      <Listbox
        value={selected}
        onChange={(value: string[]) => onChange(value)}
        multiple
        as='div'
        className={listBoxClasses({ size, disabled })}
      >
        {label && (
          <Listbox.Label
            className={labelClasses({
              size: 'md',
              isSelected: selected.length > 0 || !!placeholder,
              disabled,
              hasError,
            })}
          >
            {label}
          </Listbox.Label>
        )}
        <Listbox.Button
          className={listBoxButtonClasses({ size, disabled, hasError })}
        >
          <div className='flex-1'>{constructValue()}</div>
          <Icon Icon={'arrow_drop_down'} type='material' />
        </Listbox.Button>
        {helperText && !hasErrorMessage && (
          <span className={helperTextClasses({ disabled })}>{helperText}</span>
        )}
        {hasErrorMessage && (
          <span className={errorTextClasses({ disabled })}>{errorMessage}</span>
        )}
        <Listbox.Options
          className={listBoxOptionsClasses({ size, disabled })}
          id='optionsBox'
        >
          {options.map((it) => (
            <Listbox.Option
              key={it.value}
              value={it.value}
              className={listBoxOptionClasses({
                disabled: true,
                selected: false,
                active: false,
              })}
            >
              <div className='flex items-center gap-2'>
                <Checkbox
                  onChange={() => handleArray(it.value)}
                  label=''
                  checked={selected.includes(it.value)}
                  id={`multiple-select-checkbox-${it.value}`}
                  value={it.value}
                  variant='rounded'
                />
                {it.label}
              </div>
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </Listbox>
    </div>
  );
};

export default MultipleSelect;
